import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { getDevices } from '../api/devices';
import { MigrationDevice, getMigrationEvents } from '../api/migrations';
import PhaseStepper from '../components/PhaseStepper';
import { SectionPanel } from '../components/abPrimitives';
import { deriveDevicePhase } from '../lib/devicePhase';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { Fact, Mono, PanelShell, SectionLabel } from '../components/detailPrimitives';
import ReadinessBadge from '../components/ReadinessBadge';
import RecoveryRevealDialog from '../components/RecoveryRevealDialog';
import StatusBadge from '../components/StatusBadge';
import RelativeTime from '../components/RelativeTime';
import { ACCENT, IDENTIFIER, PLAIN_BUTTON, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-283 (FD-ADR-031) — the device detail page, SHARED between two contexts:
//
//   /devices/:serialNumber                        (Resources — global)
//   /migrations/:migrationId/devices/:serialNumber (inside a campaign)
//
// One component, two routes, because the page is the same page. What differs is
// only where the operator came FROM — and that matters: arriving from a campaign
// and landing back in the global device list on Back is disorienting, so the
// campaign-scoped route keeps its context and its back link returns there.
//
// The global route has to RESOLVE the campaign first, because a serial alone
// does not say which campaign owns it — that is precisely what the DeviceIndex
// exists to answer (FD-277).

function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 self-start font-display text-[13px] hover:underline"
      style={{ color: ACCENT.pressed }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      {label}
    </Link>
  );
}

export default function DeviceDetailPage() {
  const [revealOpen, setRevealOpen] = useState(false);
  const { serialNumber, migrationId: routeMigrationId } = useParams<{
    serialNumber: string;
    migrationId?: string;
  }>();
  const navigate = useNavigate();

  // Global route only: resolve serial → campaign. Skipped entirely when the
  // route already carries the campaign, so the campaign context costs nothing.
  const lookup = useQuery({
    queryKey: ['devices', { q: serialNumber, pageSize: 50 }],
    queryFn: () => getDevices({ q: serialNumber, pageSize: 50 }),
    enabled: !routeMigrationId && Boolean(serialNumber),
  });

  // A prefix search can return neighbours, so match the serial EXACTLY rather
  // than taking the first row — "MJ0H3P8S" would otherwise resolve to whatever
  // sorts first among MJ0H3P8S, MJ0H3P8SX, …
  const resolvedMigrationId =
    routeMigrationId ??
    lookup.data?.devices.find((d) => d.serialNumber === serialNumber)?.migrationId ??
    undefined;

  const detail = useMigrationDetailQuery(resolvedMigrationId);

  const device: MigrationDevice | undefined = useMemo(
    () => detail.data?.devices.find((d) => d.serialNumber === serialNumber),
    [detail.data, serialNumber],
  );

  const events = useQuery({
    queryKey: ['migration-events', resolvedMigrationId, device?.jobId],
    queryFn: () => getMigrationEvents(resolvedMigrationId!, device!.jobId!),
    // A device with no jobId has never booted, so there is no event trail to
    // fetch — not an empty one, none.
    enabled: Boolean(resolvedMigrationId && device?.jobId),
  });

  const backTo = routeMigrationId
    ? `/migrations/${encodeURIComponent(routeMigrationId)}/devices`
    : '/devices';
  const backLabel = routeMigrationId ? 'Campaign devices' : 'Devices';

  if (!serialNumber) return null;

  const resolving = !routeMigrationId && lookup.isLoading;
  if (resolving || detail.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={backTo} label={backLabel} />
        <Spinner size="small" label="Loading device" />
      </div>
    );
  }

  // Two genuinely different "not found" cases, and collapsing them would send an
  // operator to the wrong place. No index row at all means the serial was never
  // assigned; an index row whose campaign no longer lists the device means the
  // index is stale relative to the campaign.
  if (!resolvedMigrationId) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={backTo} label={backLabel} />
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Device not found</MessageBarTitle>
            No campaign in this deployment has{' '}
            <span className="font-mono text-xs">{serialNumber}</span> assigned.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={backTo} label={backLabel} />
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load the campaign</MessageBarTitle>
            {detail.error instanceof Error ? detail.error.message : 'Unexpected error'}
          </MessageBarBody>
          <MessageBarActions>
            <Button size="small" onClick={() => detail.refetch()}>
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={backTo} label={backLabel} />
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Device not in this campaign</MessageBarTitle>
            The device index points{' '}
            <span className="font-mono text-xs">{serialNumber}</span> at campaign{' '}
            <span className="font-mono text-xs">{resolvedMigrationId}</span>, but that campaign no
            longer lists it. The index is stale — it is repaired on the next assignment.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              size="small"
              onClick={() => navigate(`/migrations/${encodeURIComponent(resolvedMigrationId)}/overview`)}
            >
              Open the campaign
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  const migration = detail.data?.migration;

  return (
    <div className="flex flex-col gap-6">
      <BackLink to={backTo} label={backLabel} />

      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl tracking-tight" style={{ color: TEXT.primary, ...IDENTIFIER }}>
          {device.serialNumber}
        </h1>
        <StatusBadge status={device.status} />
        <ReadinessBadge status={device.readinessStatus} />
        {/* FD-313 (FD-ADR-033) — only a salvaged device offers the reveal: it is
            the machine whose ONLY working credential is the retained local admin.
            The value is never pre-fetched; the dialog stays mounted and toggles
            open (the Fluent a11y-tree gotcha). */}
        {device.status?.toLowerCase() === 'salvaged' && (
          <Button size="small" onClick={() => setRevealOpen(true)}>
            Reveal recovery password
          </Button>
        )}
      </header>

      <RecoveryRevealDialog
        open={revealOpen}
        onOpenChange={setRevealOpen}
        serialNumber={device.serialNumber}
      />

      {/* FD-467 — the migration stepper. `PhaseStepper` and `deriveDevicePhase`
          were ported by FD-466 and had NO consumer: the derivation was
          unit-tested and dead, and this page answered "where is this device"
          only by making an operator read an event list. The phase is derived
          from the device row plus its progress events in one tested pure
          function, so nothing here infers state a second time.

          It renders whether or not the events have loaded. With none, the
          derivation still places the device by its readiness verdict and its
          terminal outcome — which is the honest reading for a device that has
          not booted, rather than an empty box or a spinner over a fact we
          already know. */}
      <SectionPanel
        title="Migration progress"
        description="Derived from this device's reported progress. A stage shows as reached only when the device has reported an action in it."
      >
        <PhaseStepper view={deriveDevicePhase(device, events.data?.events ?? [])} />
      </SectionPanel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PanelShell title="Assignment">
          <Fact label="Campaign">
            <button
              type="button"
              onClick={() => navigate(`/migrations/${encodeURIComponent(resolvedMigrationId)}/overview`)}
              className={`${PLAIN_BUTTON} cursor-pointer hover:underline`}
              style={{ color: ACCENT.pressed }}
            >
              {migration?.migrationName ?? resolvedMigrationId}
            </button>
          </Fact>
          <Fact label="Stage">{migration?.migrationWave ?? '—'}</Fact>
          <Fact label="Lifecycle status">
            <StatusBadge status={device.status} />
          </Fact>
          <Fact label="Job ID">
            {device.jobId ? (
              <Mono>{device.jobId}</Mono>
            ) : (
              // A jobId is minted on the device's FIRST preflight/initialize, so
              // its absence is a fact about the device, not missing data.
              <span style={{ color: TEXT.secondary }}>Not booted yet</span>
            )}
          </Fact>
          {/* FD-503 — the Autopilot hardware hash, as PRESENCE.
              The device grid used to show a 12-character prefix of a ~4 KB blob,
              which nobody can read and nobody can copy; the column went when the
              grid was trimmed. The fact that survives the trim is the one that
              is operationally load-bearing: `ARR-APREGISTER` skips a device with
              `PRE_APHASH_MISSING` and no Graph call (FD-ADR-025 §5), so whether
              the hash arrived decides whether that device can be registered in
              the destination at all. Deleting the column without putting this
              somewhere would have removed the signal along with the noise. */}
          <Fact label="Autopilot hardware hash">
            {device.hardwareHash ? (
              <span>Reported</span>
            ) : (
              <span style={{ color: TEXT.secondary }}>Not reported</span>
            )}
          </Fact>
          <Fact label="Last event">
            {device.lastEventType ? (
              <span>
                {device.lastEventType}
                {device.lastEventTimestamp && (
                  <>
                    {' · '}
                    <RelativeTime iso={device.lastEventTimestamp} />
                  </>
                )}
              </span>
            ) : (
              <span style={{ color: TEXT.secondary }}>No events yet</span>
            )}
          </Fact>
          <Fact label="Primary user">
            {device.primaryUserDisplayName || device.primaryUserName ? (
              <span>
                {device.primaryUserDisplayName ?? device.primaryUserName}
                {device.primaryUserSamName && (
                  <span style={{ color: TEXT.secondary }}> · {device.primaryUserSamName}</span>
                )}
              </span>
            ) : (
              <span style={{ color: TEXT.secondary }}>Not reported</span>
            )}
          </Fact>
        </PanelShell>

        <PanelShell title="Preflight readiness">
          <Fact label="Verdict">
            <ReadinessBadge status={device.readinessStatus} />
          </Fact>
          {device.readinessBlockingReasons.length > 0 && (
            <Fact label="Blocking">
              <ul className="flex flex-col gap-1">
                {device.readinessBlockingReasons.map((r) => (
                  <li key={r.check} className="font-mono text-xs">
                    {r.check}
                    {r.detail && (
                      <span style={{ color: TEXT.secondary }}> — {r.detail}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Fact>
          )}
          {device.readinessWarnings.length > 0 && (
            <Fact label="Warnings">
              <ul className="flex flex-col gap-1">
                {device.readinessWarnings.map((r) => (
                  <li key={r.check} className="font-mono text-xs" style={{ color: TEXT.secondary }}>
                    {r.check}
                    {r.detail && <span> — {r.detail}</span>}
                  </li>
                ))}
              </ul>
            </Fact>
          )}
          <Fact label="Evaluated">
            {device.readinessEvaluatedUtc ? (
              <RelativeTime iso={device.readinessEvaluatedUtc} />
            ) : (
              <span style={{ color: TEXT.secondary }}>No manifest submitted yet</span>
            )}
          </Fact>
          {/* Provenance. `readinessSource` is DERIVED SERVER-SIDE precisely so
              readers do not each re-infer "empty ruleset version means floor"
              and disagree (FD-221 / FD-ADR-028 R10). */}
          <Fact label="Policy">
            {device.readinessSource === 'ruleset' ? (
              <span>
                ATC ruleset{' '}
                <Mono>{device.readinessRulesetVersion ?? 'unknown version'}</Mono>
              </span>
            ) : device.readinessSource === 'floor' ? (
              <span>
                Local baseline policy{' '}
                <Mono>{device.readinessPolicyVersion ?? 'unknown version'}</Mono>
              </span>
            ) : (
              <span style={{ color: TEXT.secondary }}>Not evaluated</span>
            )}
          </Fact>
          {device.readinessOverride && (
            <Fact label="Override">
              {/* An override RELEASES the gate; it does not rewrite the verdict
                  (FD-ADR-027). The failing reasons above stay visible — that is
                  the audit trail, and hiding them would make this page disagree
                  with the row. */}
              <span>
                Granted by <Mono>{device.readinessOverride.by}</Mono>{' '}
                <RelativeTime iso={device.readinessOverride.utc} />
                {device.readinessOverride.reason && (
                  <span style={{ color: TEXT.secondary }}>
                    {' '}
                    — {device.readinessOverride.reason}
                  </span>
                )}
              </span>
            </Fact>
          )}
        </PanelShell>
      </div>

      <section>
        <SectionLabel>Event trail</SectionLabel>
        <div style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}>
          {!device.jobId ? (
            <p className="px-4 py-6 font-display text-sm" style={{ color: TEXT.secondary }}>
              This device has not booted yet, so it has no event trail.
            </p>
          ) : events.isLoading ? (
            <div className="px-4 py-6">
              <Spinner size="tiny" label="Loading events" />
            </div>
          ) : events.isError ? (
            <p className="px-4 py-6 font-display text-sm" style={{ color: TEXT.secondary }}>
              Could not load the event trail.
            </p>
          ) : (events.data?.events.length ?? 0) === 0 ? (
            <p className="px-4 py-6 font-display text-sm" style={{ color: TEXT.secondary }}>
              No events recorded for this job yet.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: STROKE.subtle }}>
              {events.data!.events.map((e, i) => (
                <li key={e.rowKey ?? i} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2">
                  <span className="font-mono text-xs" style={{ color: TEXT.secondary }}>
                    {e.timeStamp}
                  </span>
                  <span className="font-display text-sm" style={{ color: TEXT.primary }}>
                    {e.eventSubType}
                  </span>
                  <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
                    {e.eventStatus}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {device.jobId && (
          <div className="pt-2">
            <Button
              size="small"
              appearance="subtle"
              onClick={() =>
                navigate(
                  `/migrations/${encodeURIComponent(resolvedMigrationId)}/events?jobId=${encodeURIComponent(device.jobId!)}`,
                )
              }
            >
              Open the full event log
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
